import React, { useState, useEffect } from 'react'
import { makeApiCall } from '../utils/customerUtils'
import { SettingsPageTemplate, SettingsCard, InfoTip } from '../components/SettingsPageTemplate'
import { SearchIcon, PhoneIcon, UsersIcon } from '../components/Icons'

interface PhoneNumber {
  id: number
  phone_number: string
  telnyx_phone_number_id: string
  status: 'available' | 'assigned' | 'suspended' | 'porting'
  number_type: 'local' | 'toll-free' | 'international'
  country_code: string
  area_code?: string
  region?: string
  monthly_cost?: number
  setup_cost?: number
  assigned_to_user_id?: number
  assigned_at?: string
  features: Array<'voice' | 'sms' | 'mms' | 'fax'>
  user_name?: string
}

interface AvailableNumber {
  phone_number: string
  phone_number_type: 'local' | 'toll-free' | 'international'
  country_code: string
  region: string
  locality: string
  features: Array<'voice' | 'sms' | 'mms' | 'fax'>
  setup_price: string
  monthly_price: string
}

interface User {
  id: number
  name: string
  email: string
}

const AdminPhoneNumberManagement: React.FC = () => {
  const [inventory, setInventory] = useState<PhoneNumber[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [availableNumbers, setAvailableNumbers] = useState<AvailableNumber[]>([])
  const [searchParams, setSearchParams] = useState({
    country_code: 'US',
    phone_number_type: 'local' as 'local' | 'toll-free',
    area_code: '',
    locality: ''
  })
  const [filters, setFilters] = useState({
    status: '',
    assigned_to_user_id: '',
    number_type: ''
  })
  const [loading, setLoading] = useState(true)
  const [searching, setSearching] = useState(false)
  const [purchasing, setPurchasing] = useState<string | null>(null)
  const [assigning, setAssigning] = useState<number | null>(null)
  const [unassigning, setUnassigning] = useState<number | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState<number | null>(null)
  const [assignToUserId, setAssignToUserId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    fetchInventory()
    fetchUsers()
  }, [filters, currentPage])

  // Auto-sync on initial page load
  useEffect(() => {
    syncExistingNumbers()
  }, [])

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null)
        setError(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [success, error])

  const fetchInventory = async () => {
    try {
      setLoading(true)
      setError(null)

      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20'
      })

      if (filters.status) queryParams.append('status', filters.status)
      if (filters.assigned_to_user_id) queryParams.append('assigned_to_user_id', filters.assigned_to_user_id)
      if (filters.number_type) queryParams.append('number_type', filters.number_type)

      const response = await makeApiCall(`api/phone-numbers/inventory?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch phone number inventory')
      }

      const data = await response.json()
      setInventory(data.numbers)
      setTotalPages(Math.ceil(data.total / data.limit))

    } catch (err) {
      console.error('Error fetching inventory:', err)
      setError(err instanceof Error ? err.message : 'Failed to load inventory')
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await makeApiCall('api/phone-numbers/users', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setUsers(data.users || [])
      }
    } catch (err) {
      console.error('Error fetching users:', err)
    }
  }

  const searchAvailableNumbers = async () => {
    try {
      setSearching(true)
      setError(null)
      setAvailableNumbers([])

      const queryParams = new URLSearchParams({
        country_code: searchParams.country_code,
        phone_number_type: searchParams.phone_number_type,
        limit: '10'
      })

      if (searchParams.area_code) queryParams.append('area_code', searchParams.area_code)
      if (searchParams.locality) queryParams.append('locality', searchParams.locality)

      const response = await makeApiCall(`api/phone-numbers/search?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to search phone numbers')
      }

      const data = await response.json()
      setAvailableNumbers(data.numbers || [])

    } catch (err) {
      console.error('Error searching phone numbers:', err)
      setError(err instanceof Error ? err.message : 'Failed to search phone numbers')
    } finally {
      setSearching(false)
    }
  }

  const purchasePhoneNumber = async (phoneNumber: string) => {
    try {
      setPurchasing(phoneNumber)
      setError(null)
      setSuccess(null)

      const response = await makeApiCall('api/phone-numbers/admin/purchase', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          phone_number: phoneNumber
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to purchase phone number')
      }

      setSuccess(`✅ Phone number ${phoneNumber} purchased successfully and added to inventory.`)
      setAvailableNumbers([])
      fetchInventory()

    } catch (err) {
      console.error('Error purchasing phone number:', err)
      setError(err instanceof Error ? err.message : 'Failed to purchase phone number')
    } finally {
      setPurchasing(null)
    }
  }

  const assignNumber = async () => {
    if (!showAssignModal || !assignToUserId) return

    try {
      setAssigning(showAssignModal)
      setError(null)
      setSuccess(null)

      const response = await makeApiCall('api/phone-numbers/admin/assign', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          inventory_id: showAssignModal,
          user_id: assignToUserId
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to assign phone number')
      }

      const user = users.find(u => u.id === assignToUserId)
      setSuccess(`✅ Phone number assigned to ${user?.name || 'user'} successfully.`)
      setShowAssignModal(null)
      setAssignToUserId(null)
      fetchInventory()

    } catch (err) {
      console.error('Error assigning phone number:', err)
      setError(err instanceof Error ? err.message : 'Failed to assign phone number')
    } finally {
      setAssigning(null)
    }
  }

  const unassignNumber = async (inventoryId: number) => {
    try {
      setUnassigning(inventoryId)
      setError(null)
      setSuccess(null)

      const response = await makeApiCall('api/phone-numbers/admin/unassign', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          inventory_id: inventoryId
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to unassign phone number')
      }

      setSuccess(`✅ Phone number unassigned successfully.`)
      fetchInventory()

    } catch (err) {
      console.error('Error unassigning phone number:', err)
      setError(err instanceof Error ? err.message : 'Failed to unassign phone number')
    } finally {
      setUnassigning(null)
    }
  }

  const syncExistingNumbers = async () => {
    try {
      setSyncing(true)
      setError(null)
      setSuccess(null)

      const response = await makeApiCall('api/phone-numbers/admin/sync', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to sync phone numbers')
      }

      const data = await response.json()
      // Only show success message if we're not auto-syncing on page load
      if (data.synced_count > 0) {
        setSuccess(`✅ ${data.message}`)
      }
      
      // Reset filters to show all numbers after sync
      setFilters({
        status: '',
        assigned_to_user_id: '',
        number_type: ''
      })
      setCurrentPage(1)
      
      fetchInventory()

    } catch (err) {
      console.error('Error syncing phone numbers:', err)
      setError(err instanceof Error ? err.message : 'Failed to sync phone numbers')
    } finally {
      setSyncing(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      available: 'bg-green-100 text-green-800 border-green-200',
      assigned: 'bg-blue-100 text-blue-800 border-blue-200',
      suspended: 'bg-red-100 text-red-800 border-red-200',
      porting: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    }
    
    return (
      <span className={`px-2 py-1 text-xs font-medium border rounded-full ${statusStyles[status as keyof typeof statusStyles] || statusStyles.available}`}>
        {status}
      </span>
    )
  }

  const formatPrice = (price: string | number) => {
    const num = typeof price === 'string' ? parseFloat(price) : price
    return isNaN(num) ? 'N/A' : `$${num.toFixed(2)}`
  }

  const messageState = error ? { type: 'error' as const, text: error } : 
                       success ? { type: 'success' as const, text: success } : null

  return (
    <SettingsPageTemplate
      title="Phone Number Management (Admin)"
      description="Manage phone number inventory, assignments, and purchase new numbers"
      message={messageState}
      loading={loading}
    >
      <div className="space-y-6">
        
        {/* Auto-sync Info */}
        <InfoTip type="info">
          <strong>💡 Automatic Sync:</strong><br/>
          Phone numbers from your Telnyx account are automatically synced when you open this page. 
          Your admin default SMS number will be marked as "suspended" (not assignable to users).
        </InfoTip>

        {/* Inventory Overview */}
        <SettingsCard title="📊 Phone Number Inventory">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{inventory.filter(n => n.status === 'available').length}</div>
              <div className="text-sm text-green-700 dark:text-green-300">Available</div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{inventory.filter(n => n.status === 'assigned').length}</div>
              <div className="text-sm text-blue-700 dark:text-blue-300">Assigned</div>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{inventory.filter(n => n.status === 'suspended').length}</div>
              <div className="text-sm text-red-700 dark:text-red-300">Suspended</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/20 p-4 rounded-lg">
              <div className="text-2xl font-bold text-gray-600">{inventory.length}</div>
              <div className="text-sm text-gray-700 dark:text-gray-300">Total</div>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="">All Statuses</option>
                <option value="available">Available</option>
                <option value="assigned">Assigned</option>
                <option value="suspended">Suspended</option>
                <option value="porting">Porting</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Assigned User
              </label>
              <select
                value={filters.assigned_to_user_id}
                onChange={(e) => setFilters(prev => ({ ...prev, assigned_to_user_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="">All Users</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Type
              </label>
              <select
                value={filters.number_type}
                onChange={(e) => setFilters(prev => ({ ...prev, number_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="">All Types</option>
                <option value="local">Local</option>
                <option value="toll-free">Toll-Free</option>
                <option value="international">International</option>
              </select>
            </div>
          </div>

          {/* Phone Numbers Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left p-3">Phone Number</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Assigned User</th>
                  <th className="text-left p-3">Monthly Cost</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {inventory.map((number) => (
                  <tr key={number.id} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="p-3">
                      <div className="font-medium">{number.phone_number}</div>
                      <div className="text-xs text-gray-500">{number.area_code && `Area: ${number.area_code}`}</div>
                    </td>
                    <td className="p-3">{number.number_type}</td>
                    <td className="p-3">{getStatusBadge(number.status)}</td>
                    <td className="p-3">
                      {number.user_name ? (
                        <div>
                          <div className="font-medium">{number.user_name}</div>
                          {number.assigned_at && (
                            <div className="text-xs text-gray-500">
                              {new Date(number.assigned_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">Unassigned</span>
                      )}
                    </td>
                    <td className="p-3">{formatPrice(number.monthly_cost || 0)}</td>
                    <td className="p-3">
                      <div className="flex space-x-2">
                        {number.status === 'available' ? (
                          <button
                            onClick={() => setShowAssignModal(number.id)}
                            className="text-gray-900 hover:text-blue-800 font-medium"
                          >
                            Assign
                          </button>
                        ) : number.status === 'assigned' ? (
                          <button
                            onClick={() => unassignNumber(number.id)}
                            disabled={unassigning === number.id}
                            className="text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                          >
                            {unassigning === number.id ? 'Unassigning...' : 'Unassign'}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-6">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </SettingsCard>

        {/* Search and Purchase Section */}
        <div id="search-section">
          <SettingsCard title="🔍 Purchase New Numbers">
          <InfoTip type="info">
            Search for available numbers to add to your inventory. These numbers will be available for assignment to users.
          </InfoTip>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Country
              </label>
              <select
                value={searchParams.country_code}
                onChange={(e) => setSearchParams(prev => ({ ...prev, country_code: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Type
              </label>
              <select
                value={searchParams.phone_number_type}
                onChange={(e) => setSearchParams(prev => ({ ...prev, phone_number_type: e.target.value as 'local' | 'toll-free' }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="local">Local</option>
                <option value="toll-free">Toll-Free</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Area Code
              </label>
              <input
                type="text"
                value={searchParams.area_code}
                onChange={(e) => setSearchParams(prev => ({ ...prev, area_code: e.target.value }))}
                placeholder="e.g., 514"
                maxLength={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={searchAvailableNumbers}
                disabled={searching}
                className="w-full flex items-center justify-center px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 transition-colors disabled:opacity-50"
              >
                <SearchIcon className="mr-2" size={16} />
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>

          {/* Available Numbers */}
          {availableNumbers.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 dark:text-white">Available for Purchase:</h4>
              {availableNumbers.map((number) => (
                <div
                  key={number.phone_number}
                  className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {number.phone_number}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {number.locality}, {number.region} • {number.phone_number_type}
                    </div>
                  </div>
                  
                  <div className="text-right mr-4">
                    <div className="text-sm font-medium">{formatPrice(number.setup_price)} setup</div>
                    <div className="text-xs text-gray-600">{formatPrice(number.monthly_price)}/month</div>
                  </div>

                  <button
                    onClick={() => purchasePhoneNumber(number.phone_number)}
                    disabled={purchasing === number.phone_number}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors disabled:opacity-50"
                  >
                    {purchasing === number.phone_number ? 'Purchasing...' : 'Purchase'}
                  </button>
                </div>
              ))}
            </div>
          )}
          </SettingsCard>
        </div>

      </div>

      {/* Assignment Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Assign Phone Number
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select User
              </label>
              <select
                value={assignToUserId || ''}
                onChange={(e) => setAssignToUserId(Number(e.target.value) || null)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="">Select a user...</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                ))}
              </select>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowAssignModal(null)
                  setAssignToUserId(null)
                }}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={assignNumber}
                disabled={!assignToUserId || assigning === showAssignModal}
                className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                {assigning === showAssignModal ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </SettingsPageTemplate>
  )
}

export default AdminPhoneNumberManagement
